import * as React from 'react';
import {
  Pressable,
  type PressableProps,
  Text as NativeText,
  type TextProps as NativeTextProps,
  View,
  type ViewProps,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import {
  SafeAreaView,
  type SafeAreaViewProps,
} from 'react-native-safe-area-context';
import { useAppTheme, type Theme } from '@/theme';
import {
  resolveCompatibilityStyle,
  type StylePropValue,
} from '@/lib/styleSystem';
import FieldBackground from './FieldBackground';

type SpacingValue = keyof Theme['spacing'] | number | string;
type ColorValue = keyof Theme['colors'] | string;
type RadiusValue = keyof Theme['borderRadii'] | number;

type CompatibilityProps = {
  margin?: SpacingValue;
  marginTop?: SpacingValue;
  marginRight?: SpacingValue;
  marginBottom?: SpacingValue;
  marginLeft?: SpacingValue;
  marginHorizontal?: SpacingValue;
  marginVertical?: SpacingValue;
  padding?: SpacingValue;
  paddingTop?: SpacingValue;
  paddingRight?: SpacingValue;
  paddingBottom?: SpacingValue;
  paddingLeft?: SpacingValue;
  paddingHorizontal?: SpacingValue;
  paddingVertical?: SpacingValue;
  gap?: SpacingValue;
  rowGap?: SpacingValue;
  columnGap?: SpacingValue;
  backgroundColor?: ColorValue;
  color?: ColorValue;
  borderColor?: ColorValue;
  borderTopColor?: ColorValue;
  borderRightColor?: ColorValue;
  borderBottomColor?: ColorValue;
  borderLeftColor?: ColorValue;
  borderRadius?: RadiusValue;
  borderWidth?: number;
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderBottomWidth?: number;
  borderLeftWidth?: number;
  flex?: number;
  flexBasis?: number | string;
  flexGrow?: number;
  flexShrink?: number;
  flexDirection?: ViewStyle['flexDirection'];
  flexWrap?: ViewStyle['flexWrap'];
  alignItems?: ViewStyle['alignItems'];
  alignSelf?: ViewStyle['alignSelf'];
  justifyContent?: ViewStyle['justifyContent'];
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  height?: number | string;
  minHeight?: number | string;
  maxHeight?: number | string;
  position?: ViewStyle['position'];
  top?: number | string;
  right?: number | string;
  bottom?: number | string;
  left?: number | string;
  zIndex?: number;
  overflow?: ViewStyle['overflow'];
  opacity?: number;
  shadowColor?: string;
  shadowOffset?: { width: number; height: number };
  shadowOpacity?: number;
  shadowRadius?: number;
  elevation?: number;
  textAlign?: TextStyle['textAlign'];
  fontWeight?: TextStyle['fontWeight'] | 'medium' | 'semibold' | 'extrabold';
  fontSize?: number;
  lineHeight?: number;
  letterSpacing?: number;
  textTransform?: TextStyle['textTransform'];
  textDecorationLine?: 'none' | 'underline' | 'line-through' | 'underline line-through';
};

function removeEmptyTransform(style: unknown): unknown {
  if (Array.isArray(style)) {
    return style
      .map(removeEmptyTransform)
      .filter((styleValue) => styleValue !== null && styleValue !== undefined && styleValue !== false);
  }

  if (!style || typeof style !== 'object') {
    return style;
  }

  if (!('transform' in style) || style.transform != null) {
    return style;
  }

  const { transform: _transform, ...styleWithoutTransform } = style;
  return styleWithoutTransform;
}

export type BoxProps = ViewProps &
  CompatibilityProps & {
    style?: StylePropValue;
    textured?: boolean;
  };

export type PressableBoxProps = PressableProps &
  CompatibilityProps & {
    style?: StylePropValue;
  };

export type SafeAreaBoxProps = SafeAreaViewProps &
  CompatibilityProps & {
    style?: StylePropValue;
    textured?: boolean;
  };

export type TextProps = NativeTextProps &
  CompatibilityProps & {
    variant?: keyof Theme['textVariants'];
    style?: StylePropValue;
  };

function splitCompatibilityProps<T extends CompatibilityProps>(props: T) {
  const {
    margin,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    marginHorizontal,
    marginVertical,
    padding,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    paddingHorizontal,
    paddingVertical,
    gap,
    rowGap,
    columnGap,
    backgroundColor,
    color,
    borderColor,
    borderTopColor,
    borderRightColor,
    borderBottomColor,
    borderLeftColor,
    borderRadius,
    borderWidth,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    flex,
    flexBasis,
    flexGrow,
    flexShrink,
    flexDirection,
    flexWrap,
    alignItems,
    alignSelf,
    justifyContent,
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
    maxHeight,
    position,
    top,
    right,
    bottom,
    left,
    zIndex,
    overflow,
    opacity,
    shadowColor,
    shadowOffset,
    shadowOpacity,
    shadowRadius,
    elevation,
    textAlign,
    fontWeight,
    fontSize,
    lineHeight,
    letterSpacing,
    textTransform,
    textDecorationLine,
    ...rest
  } = props;

  return {
    compatibilityProps: {
      margin,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
      marginHorizontal,
      marginVertical,
      padding,
      paddingTop,
      paddingRight,
      paddingBottom,
      paddingLeft,
      paddingHorizontal,
      paddingVertical,
      gap,
      rowGap,
      columnGap,
      backgroundColor,
      color,
      borderColor,
      borderTopColor,
      borderRightColor,
      borderBottomColor,
      borderLeftColor,
      borderRadius,
      borderWidth,
      borderTopWidth,
      borderRightWidth,
      borderBottomWidth,
      borderLeftWidth,
      flex,
      flexBasis,
      flexGrow,
      flexShrink,
      flexDirection,
      flexWrap,
      alignItems,
      alignSelf,
      justifyContent,
      width,
      minWidth,
      maxWidth,
      height,
      minHeight,
      maxHeight,
      position,
      top,
      right,
      bottom,
      left,
      zIndex,
      overflow,
      opacity,
      shadowColor,
      shadowOffset,
      shadowOpacity,
      shadowRadius,
      elevation,
      textAlign,
      fontWeight:
        fontWeight === 'medium'
          ? '500'
          : fontWeight === 'semibold'
            ? '600'
            : fontWeight === 'extrabold'
              ? '800'
              : fontWeight,
      fontSize,
      lineHeight,
      letterSpacing,
      textTransform,
      textDecorationLine,
    },
    rest,
  };
}

export const Box = React.forwardRef<View, BoxProps>((props, ref) => {
  const theme = useAppTheme();
  const { textured, ...remainingProps } = props;
  const { compatibilityProps, rest } = splitCompatibilityProps(remainingProps);
  
  // Keep the app ground quiet by default. Textured backgrounds remain opt-in
  // for surfaces that intentionally need field materiality.
  const isBackground = compatibilityProps.backgroundColor === 'background' && textured === true;
  
  const resolvedProps = {
    ...compatibilityProps,
    backgroundColor: isBackground ? 'transparent' : compatibilityProps.backgroundColor,
  };
  
  const style = resolveCompatibilityStyle(resolvedProps, theme);

  if (isBackground) {
    const { children, ...restWithoutChildren } = rest;
    return (
      <View ref={ref} {...restWithoutChildren} style={removeEmptyTransform([style, props.style]) as any}>
        <FieldBackground />
        {children}
      </View>
    );
  }

  return <View ref={ref} {...rest} style={removeEmptyTransform([style, props.style]) as any} />;
});

Box.displayName = 'Box';

export const PressableBox = React.forwardRef<React.ElementRef<typeof Pressable>, PressableBoxProps>(
  (props, ref) => {
  const theme = useAppTheme();
  const { compatibilityProps, rest } = splitCompatibilityProps(props);
  const style = resolveCompatibilityStyle(compatibilityProps, theme);

  return <Pressable ref={ref} {...rest} style={removeEmptyTransform([style, props.style]) as any} />;
});

PressableBox.displayName = 'PressableBox';

export const SafeAreaBox = React.forwardRef<React.ElementRef<typeof SafeAreaView>, SafeAreaBoxProps>(
  (props, ref) => {
  const theme = useAppTheme();
  const { textured, ...remainingProps } = props;
  const { compatibilityProps, rest } = splitCompatibilityProps(remainingProps);
  
  const isBackground = compatibilityProps.backgroundColor === 'background' && textured === true;
  
  const resolvedProps = {
    ...compatibilityProps,
    backgroundColor: isBackground ? 'transparent' : compatibilityProps.backgroundColor,
  };
  
  const style = resolveCompatibilityStyle(resolvedProps, theme);

  if (isBackground) {
    const { children, ...restWithoutChildren } = rest;
    return (
      <SafeAreaView ref={ref} {...restWithoutChildren} style={removeEmptyTransform([style, props.style]) as any}>
        <FieldBackground />
        {children}
      </SafeAreaView>
    );
  }

  return <SafeAreaView ref={ref} {...rest} style={removeEmptyTransform([style, props.style]) as any} />;
});

SafeAreaBox.displayName = 'SafeAreaBox';

export const Text = React.forwardRef<NativeText, TextProps>((props, ref) => {
  const theme = useAppTheme();
  const { variant = 'defaults', ...remainingProps } = props;
  const { compatibilityProps, rest } = splitCompatibilityProps(remainingProps);
  const style = resolveCompatibilityStyle(compatibilityProps, theme);
  const variantStyle = theme.textVariants[variant] ?? theme.textVariants.defaults;

  return <NativeText ref={ref} {...rest} style={removeEmptyTransform([variantStyle, style, props.style]) as any} />;
});

Text.displayName = 'Text';
